import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/utils';
// import Link from 'next/link';

const buttonVariants = cva(
  'inline-flex items-center justify-center text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-jen-orange text-white hover:bg-jen-orange/80 transition duration-300',
        secondary:
          'text-secondary-foreground bg-transparent hover:border-jen-orange/80 border border-jen-grey-2',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-jen-grey-1-border text-jen-content-secondary hover:border-jen-orange/[50%]',

        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-jen-orange bg-transparent',
        fullscreen: 'h-10 w-10 bg-jen-grey-2 hover:bg-jen-grey-1 p-2 rounded-xl',
        tag: 'bg-transparent text-jen-tag-purple border border-jen-tag-purple text-xs uppercase font-secondary',
        tagDisabled: 'bg-transparent text-jen-content-primary border border-jen-content-primary text-xs uppercase font-secondary',
        tagComingSoon: 'bg-transparent text-jen-content-primary border border-jen-content-primary text-[12px] leading-[90%] uppercase font-secondary cursor-default',
      },
      size: {
        default: 'p-[16px] gap-2 rounded-lg',
        sm: 'h-9 rounded-md px-3',
        lg: 'p-[14px] pr-[16px] md:p-[24px] md:pr-[28px] gap-2 rounded-lg',
        icon: 'h-10 w-10',
        tag: 'p-[8px] pt-[6px] rounded-md',
        tagDisabled: 'p-[3px] pt-[2px] rounded-sm',
        tagComingSoon: 'p-[2px] py-[1px] rounded-[2px]',
        link: 'p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  link?: string;
  target?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, link, target, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <a href={link} target={target} className='flex justify-center'>
        <Comp
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...props}
        />
      </a>

    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };